import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api,{formatApiErrorDetail} from "@/lib/api";
import MetaLeadIntake from "@/components/MetaLeadIntake";
import MetaLeadAdStager from "@/components/MetaLeadAdStager";
import MetaInsightsPanel from "@/components/MetaInsightsPanel";
import { Clapperboard,Image as ImageIcon,BarChart3,Globe,ShieldCheck,Megaphone,RefreshCcw } from "lucide-react";

const field="mt-1 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm";
const button="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40";

export default function MarketingOperations(){
 const [drafts,setDrafts]=useState([]);
 const [campaigns,setCampaigns]=useState([]);
 const [draftId,setDraftId]=useState("");
 const [campaignId,setCampaignId]=useState("");
 const [tab,setTab]=useState("media");
 const [status,setStatus]=useState({});
 const [media,setMedia]=useState([]);
 const [mediaReviewRefs,setMediaReviewRefs]=useState({});
 const [assets,setAssets]=useState([]);
 const [stats,setStats]=useState(null);
 const [staged,setStaged]=useState([]);
 const [preview,setPreview]=useState({});
 const [kind,setKind]=useState("image");
 const [aspect,setAspect]=useState("landscape");
 const [prompt,setPrompt]=useState("An optimistic, professional, elegant and dignified Florida family planning for the future; no written text or logos");
 const [network,setNetwork]=useState("meta");
 const [budget,setBudget]=useState("1000");
 const [review,setReview]=useState("");
 const [spend,setSpend]=useState({channel:"meta",amount:"",source_reference:"",description:""});
 const [seo,setSeo]=useState({asset:"",variant:"0",review:""});
 const [busy,setBusy]=useState(false);
 const [loading,setLoading]=useState(true);
 const [notice,setNotice]=useState("");
 const [error,setError]=useState("");

 const refresh=useCallback(async()=>{
   const [a,b,c,d]=await Promise.all([
     api.get("/platform/campaign-director/drafts"),
     api.get("/platform/lead-marketplace/campaigns"),
     api.get("/platform/marketing/integrations"),
     api.get("/platform/marketing/media/capabilities"),
   ]);
   setDrafts(a.data||[]);setCampaigns(b.data||[]);
   setDraftId(current=>current||(a.data||[])[0]?.id||"");
   setCampaignId(current=>current||(b.data||[])[0]?.id||"");
   setStatus({...(c.data||{}),media:d.data||{}});
 },[]);
 useEffect(()=>{
   refresh().catch(e=>setError(formatApiErrorDetail(e?.response?.data?.detail)||"Unable to load marketing integrations."))
     .finally(()=>setLoading(false));
 },[refresh]);
 useEffect(()=>{
   if(!draftId){setMedia([]);setAssets([]);return;}
   let active=true;
   Promise.all([
     api.get(`/platform/marketing/media/drafts/${draftId}`),
     api.get(`/platform/campaign-director/drafts/${draftId}/assets`),
   ]).then(([a,b])=>{if(active){setMedia(a.data||[]);setAssets(b.data||[]);}})
     .catch(()=>{if(active)setError("One or more marketing creative libraries are unavailable.");});
   return()=>{active=false};
 },[draftId]);
 useEffect(()=>{
   if(!campaignId){setStats(null);return;}
   let active=true;
   api.get(`/platform/marketing/campaigns/${campaignId}/performance`)
     .then(({data})=>{if(active)setStats(data)})
     .catch(()=>{if(active)setStats(null);});
   return()=>{active=false};
 },[campaignId]);
 useEffect(()=>{
   api.get("/platform/marketing/staged-ads").then(({data})=>setStaged(data||[])).catch(()=>{});
 },[]);

 async function task(fn,success){
   setBusy(true);setError("");setNotice("");
   try{const data=await fn();if(success)setNotice(success);return data;}
   catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"This request could not be completed.");return null;}
   finally{setBusy(false);}
 }
 const selectedDraft=drafts.find(d=>d.id===draftId);
 const selectedCampaign=campaigns.find(c=>c.id===campaignId);
 const isConfigured=(name)=>Boolean(status[name]?.connected||status.media?.[name+"_configured"]);

 const createMedia=async()=>{
   if(!draftId||prompt.trim().length<15)return;
   const ready=kind==="image"?status.media?.image_configured:status.media?.video_configured;
   if(!ready){setError("This paid media provider is not connected or enabled in deployment.");return;}
   if(!window.confirm(`Generate a ${kind} with ${kind==="video"?"Runway":"OpenAI"} credits? This will incur provider charges. Nothing will be published or sent. Continue?`))return;
   const result=await task(async()=>{
     const r=await api.post(`/platform/marketing/media/drafts/${draftId}`,{kind,aspect,prompt});
     setMedia(current=>[r.data,...current]);
     return r.data;
   },`${kind==="image"?"Image":"Video task"} created. Review before any use.`);
   return result;
 };
 const approveVisual=async(item)=>{
   const reviewReference=(mediaReviewRefs[item.id]||"").trim();
   if(!reviewReference||reviewReference.length<8||busy)return;
   if(!window.confirm("Confirm the exact media was independently reviewed for accuracy, rights, branding and advertising compliance? This will NOT publish it, buy ads or contact anyone."))return;
   await task(async()=>{
     const {data}=await api.post(`/platform/marketing/media/${item.id}/approve`,{
       review_reference:reviewReference,confirm_legal_and_visual_review:true
     });
     setMedia(items=>items.map(x=>x.id===item.id?{...x,review_approved:data.review_approved,
       approval_reference:reviewReference}:x));
     return data;
   },"Reviewed media approval recorded. The file remains private and unpublished.");
 };
 const openImage=async(item)=>{
   const blob=await task(async()=>{
     const r=await api.get(`/platform/marketing/media/${item.id}/image`,{responseType:"blob"});
     const url=URL.createObjectURL(r.data);
     setPreview(current=>({...current,[item.id]:url}));
     return url;
   });
   return blob;
 };
 const refreshVideo=async(item)=>{
   await task(async()=>{
     const r=await api.post(`/platform/marketing/media/${item.id}/refresh`);
     setMedia(current=>current.map(x=>x.id===item.id?{...x,...r.data}:x));
   },"Video task status updated.");
 };
 const stageAd=async()=>{
   if(!selectedCampaign)return;
   if(!status[network]?.connected||!status[network]?.staging_enabled){
     setError("The selected advertising provider has not been connected and enabled.");return;
   }
   if(!window.confirm(`Create a PAUSED ${network.toUpperCase()} campaign in your connected account, with a proposed daily limit of $ ${(Number(budget)/100).toFixed(2)}? This does not enable advertising. Provider account setup may still create resource commitments.`))return;
   await task(async()=>{
     const r=await api.post("/platform/marketing/stage-ad",{
       campaign_id:campaignId,network,review_reference:review,daily_budget_cents:Number(budget)});
     setStaged(current=>[r.data,...current]);return r.data;
   },"Paused provider campaign staged. No ads are serving.");
 };
 const addSpend=async()=>{
   if(!campaignId)return;
   if(!window.confirm("Record this expense as verified marketing spend? It must match an actual invoice or provider report."))return;
   await task(async()=>{
     await api.post("/platform/marketing/spend",{
       campaign_id:campaignId,channel:spend.channel,amount_cents:Math.round(Number(spend.amount)*100),
       source_reference:spend.source_reference,description:spend.description});
     const {data}=await api.get(`/platform/marketing/campaigns/${campaignId}/performance`);
     setStats(data);
     setSpend({channel:"meta",amount:"",source_reference:"",description:""});
   },"Verified expense recorded in the immutable spend ledger.");
 };
 const wordpress=async()=>{
   if(!status.wordpress?.connected||!status.wordpress?.draft_enabled){setError("WordPress draft connection is disabled.");return;}
   if(!window.confirm("Create an UNPUBLISHED WordPress post draft from this AI copy? This uses the configured WordPress account and does not publish publicly."))return;
   await task(()=>api.post("/platform/marketing/wordpress-draft",{
     asset_id:seo.asset,variant_index:Number(seo.variant),review_reference:seo.review
   }),"Unpublished WordPress draft created. Review it in WordPress.");
 };
 const organic=assets.filter(x=>x.channel==="organic_search");
 const cardCls="min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
 const money=cents=>cents==null?"Unavailable":new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(cents/100);
 if(loading)return <div className="p-6">Loading marketing operations…</div>;
 return <main className="mx-auto w-full max-w-6xl space-y-5 pb-12" data-testid="marketing-operations-page">
   <header className="rounded-[20px] bg-[#0B1B2C] p-5 text-white sm:p-8">
     <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D1A656]">Fortunate Operations / Growth</p>
     <h1 className="mt-2 font-heading text-3xl font-bold">Marketing Operations</h1>
     <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Create visuals, stage provider campaigns, prepare search content and measure your real lead economics. Every paid or external action requires explicit approval.</p>
   </header>
   {error&&<p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
   {notice&&<p role="status" className="rounded-lg border bg-white p-3 text-sm">{notice}</p>}
   <div className="flex flex-wrap gap-2" role="tablist" aria-label="Marketing operations sections">
     {[["media","Visual studio",ImageIcon],["channels","Ad channels",Megaphone],["meta-leads","Receive leads",ShieldCheck],["search","SEO publishing",Globe],["performance","Performance",BarChart3]].map(([id,title,Icon])=>
       <button key={id} type="button" role="tab" aria-selected={tab===id}
         onClick={()=>setTab(id)} data-testid={`marketing-tab-${id}`}
         className={`${button} ${tab===id?"border-[#D1A656] bg-[#FBF7EF] text-[#0B1B2C]":"bg-white"}`}>
         <Icon className="h-4 w-4"/>{title}</button>)}
   </div>
   {(tab==="media"||tab==="search")&&<label className="block max-w-lg text-sm font-semibold">Campaign strategy
      <select className={field} value={draftId} onChange={e=>setDraftId(e.target.value)}>
       {!drafts.length&&<option value="">No saved strategies</option>}
       {drafts.map(d=><option key={d.id} value={d.id}>{d.proposal?.name||d.id}</option>)}
      </select>
    </label>}
   {(tab==="channels"||tab==="performance")&&<label className="block max-w-lg text-sm font-semibold">First-party campaign
      <select className={field} value={campaignId} onChange={e=>setCampaignId(e.target.value)}>
       {!campaigns.length&&<option value="">No reviewed campaigns</option>}
       {campaigns.map(c=><option key={c.id} value={c.id}>{c.campaign_name}</option>)}
      </select>
    </label>}
   {tab==="media"&&<section className="space-y-4" data-testid="marketing-media">
      <div className={cardCls}>
        <h2 className="font-heading text-lg font-bold">Visual & video creation</h2>
        <p className="mt-1 text-xs text-slate-500">OpenAI image and Runway video tasks are paid on explicit request. No auto-generation.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">Output
            <select className={field} value={kind} onChange={e=>{const value=e.target.value;setKind(value);if(value==="video"&&aspect==="square")setAspect("landscape");}} data-testid="media-kind">
              <option value="image">Marketing image</option><option value="video">5-second video</option>
            </select>
          </label>
          <label className="text-sm">Aspect ratio
            <select className={field} value={aspect} onChange={e=>setAspect(e.target.value)}>
              <option value="landscape">Landscape</option>{kind==="image"&&<option value="square">Square</option>}<option value="portrait">Portrait</option>
            </select>
          </label>
        </div>
        <label className="mt-3 block text-sm">Creative brief
          <textarea className={field} rows={3} maxLength={1500} value={prompt} onChange={e=>setPrompt(e.target.value)}/>
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" className={button+" border-[#D1A656] bg-[#D1A656]"} disabled={busy||!draftId||
            !(kind==="image"?status.media?.image_configured:status.media?.video_configured)}
            onClick={createMedia} data-testid="marketing-generate-media">
            <Clapperboard className="h-4 w-4"/>Generate {kind} (paid credits)
          </button>
          <span className="text-xs text-slate-500">{(kind==="image"?status.media?.image_configured:status.media?.video_configured)?"Provider configured":"Provider disabled or not connected"}</span>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {media.map(item=><article key={item.id} className={cardCls} data-testid="marketing-media-item">
          <strong className="text-sm capitalize">{item.kind} · {item.aspect}</strong>
          <p className="mt-1 text-xs text-slate-500">Status: {item.review_approved?"Approved, unpublished":item.status+" · Unpublished"}</p>
          <p className="mt-2 text-sm">{item.prompt}</p>
          {item.kind==="image"&&<>
             {preview[item.id]&&<img src={preview[item.id]} alt="Generated review-only marketing creative" className="my-3 max-h-[400px] w-full rounded-lg object-contain"/>}
             <button type="button" className={button+" mt-2"} onClick={()=>openImage(item)}>View secure image</button>
          </>}
          {item.kind==="video"&&<div className="mt-2 space-y-2">
            <button type="button" className={button} onClick={()=>refreshVideo(item)}>Refresh Runway task</button>
            {(item.video_urls||[]).map((url,i)=><a key={i} href={url} target="_blank" rel="noreferrer" className="block break-all text-sm underline">View rendered video {i+1} (temporary provider link)</a>)}
          </div>}
          {!item.review_approved && item.status==="review_required"&&<div className="mt-3 space-y-2 border-t pt-3">
            <p className="text-xs text-slate-600">After reviewing the rendered media and applicable rights, enter the independent review reference.</p>
            <input className={field} value={mediaReviewRefs[item.id]||""} maxLength={200}
              onChange={e=>setMediaReviewRefs(old=>({...old,[item.id]:e.target.value}))}
              placeholder="Visual review reference"/>
            <button type="button" className={button+" mt-2"} disabled={busy||(mediaReviewRefs[item.id]||"").trim().length<8}
              onClick={()=>approveVisual(item)} data-testid="marketing-approve-media">Approve visual, keep unpublished</button>
          </div>}
          {item.review_approved&&<p className="mt-2 text-xs text-slate-600">Approval: {item.approval_reference||"recorded"} · No advertising spend or publishing</p>}
        </article>)}
      </div>
      {!media.length&&<p className="text-sm text-slate-500">No generated media for this campaign yet.</p>}
    </section>}
   {tab==="meta-leads"&&<MetaLeadIntake campaigns={campaigns}/>}
   {tab==="channels"&&<section className="space-y-4" data-testid="marketing-ad-channels">
      <MetaLeadAdStager campaigns={campaigns}/>
      <div className="grid gap-3 md:grid-cols-2">
        {["meta","google"].map(id=><div key={id} className={cardCls}>
          <h2 className="font-heading text-lg font-bold">{id==="meta"?"Meta / Facebook / Instagram":"Google Ads"}</h2>
          <p className="mt-1 text-xs text-slate-500">{status[id]?.connected?"Account credentials configured":"Account not connected"} · {status[id]?.staging_enabled?"Paused staging permitted":"Staging disabled"}</p>
          <p className="mt-3 text-sm text-slate-600">Creates a paused campaign structure for review. No ad sets or ads are launched, and spend is not enabled.</p>
        </div>)}
      </div>
      <div className={cardCls}>
        <h3 className="font-heading text-lg font-bold">Stage a reviewed campaign</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="text-sm">Ad network
            <select className={field} value={network} onChange={e=>setNetwork(e.target.value)} data-testid="stage-network">
              <option value="meta">Meta</option><option value="google">Google Ads</option>
            </select>
          </label>
          <label className="text-sm">Proposed daily budget (cents)
            <input className={field} type="number" min="500" max="200000" value={budget} onChange={e=>setBudget(e.target.value)}/>
          </label>
          <label className="text-sm">Independent advertising review reference
            <input className={field} value={review} onChange={e=>setReview(e.target.value)}/>
          </label>
        </div>
        <button type="button" className={button+" mt-4 border-[#D1A656] bg-[#D1A656]"}
          disabled={busy||!campaignId||!status[network]?.connected||!status[network]?.staging_enabled||
            review.length<8||Number(budget)<500}
          onClick={stageAd} data-testid="marketing-stage-ad">Stage PAUSED provider campaign</button>
        <p className="mt-3 text-xs text-amber-900">These provider campaigns are not full ads. Ad creatives, targeting, platform review, and a separate live-publication process are required.</p>
      </div>
      {staged.length>0&&<div className={cardCls}>
        <h3 className="mb-3 font-semibold">Staged advertising accounts</h3>
        {staged.map(s=><div key={s.id} className="border-t py-3 text-sm">
          <strong>{s.network.toUpperCase()}</strong> · {s.status} · {s.provider?.resource_id}
          <span className="ml-2 text-xs text-slate-500">Never auto-activated</span>
        </div>)}
      </div>}
    </section>}
   {tab==="search"&&<section className={cardCls} data-testid="marketing-seo">
      <h2 className="font-heading text-xl font-bold">Search content & WordPress drafts</h2>
      <p className="mt-2 text-sm text-slate-600">Create original search-oriented copy in <Link className="underline" to="/content-studio">Content Studio</Link>. Send independently reviewed creative to WordPress as an unpublished post draft.</p>
      <p className="mt-3 text-xs text-slate-500">WordPress: {status.wordpress?.connected?"Credentials present":"Not connected"} · {status.wordpress?.draft_enabled?"Draft staging on":"Draft staging off"}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <label className="text-sm">SEO content asset
          <select className={field} value={seo.asset} onChange={e=>setSeo(s=>({...s,asset:e.target.value}))}>
            <option value="">Select a search draft</option>
            {organic.map(a=><option key={a.id} value={a.id}>{a.id.slice(0,8)} · {a.language}</option>)}
          </select>
        </label>
        <label className="text-sm">Variant
          <select className={field} value={seo.variant} onChange={e=>setSeo(s=>({...s,variant:e.target.value}))}>
            <option value="0">Variant 1</option><option value="1">Variant 2</option><option value="2">Variant 3</option>
          </select>
        </label>
        <label className="text-sm">Human content review reference
          <input className={field} value={seo.review} onChange={e=>setSeo(s=>({...s,review:e.target.value}))}/>
        </label>
      </div>
      <button type="button" className={button+" mt-4"}
        disabled={busy||!seo.asset||seo.review.length<8||!status.wordpress?.connected||!status.wordpress?.draft_enabled}
        onClick={wordpress} data-testid="marketing-wordpress-draft">Send UNPUBLISHED draft to WordPress</button>
      <p className="mt-3 text-xs text-slate-500">This is content creation and draft staging—not a guarantee of Google rankings or automatic SEO optimization.</p>
    </section>}
   {tab==="performance"&&<section className="space-y-4" data-testid="marketing-performance">
      <MetaInsightsPanel campaignId={campaignId}/>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["Captured inquiries",stats?.captured_inquiries??"—"],
          ["Qualified inquiries",stats?.qualified_inquiries??"—"],
          ["Reserved exclusive leads",stats?.reserved_exclusive??"—"],
          ["Verified marketing spend",money(stats?.manual_verified_spend_cents)],
          ["Cost per inquiry",money(stats?.cost_per_inquiry_cents)],
          ["Cost per qualified inquiry",money(stats?.cost_per_qualified_inquiry_cents)],
        ].map(([name,value])=><div key={name} className={cardCls}>
          <p className="text-xs uppercase tracking-wide text-slate-500">{name}</p>
          <strong className="mt-3 block font-heading text-2xl text-[#0B1B2C]">{value}</strong>
        </div>)}
      </div>
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="roas-not-yet">
        {stats?.roas == null
          ? "ROAS / profit cannot be reported until verified delivered-lead revenue is linked to paid marketing spend. Prepaid orders and reservations are not automatically counted as earned profit."
          : `Gross delivered-lead return on verified ad spend: ${Number(stats.roas).toFixed(2)}× (before refunds, fees, chargebacks or operating costs). This is NOT net profit.`}
      </p>
      <div className={cardCls}>
        <h2 className="font-heading text-lg font-semibold">Record verified acquisition spend</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">Source
            <select className={field} value={spend.channel} onChange={e=>setSpend(s=>({...s,channel:e.target.value}))}>
              {["meta","google","seo","content","other"].map(x=><option key={x}>{x}</option>)}
            </select>
          </label>
          <label className="text-sm">Verified cost in USD
            <input className={field} type="number" min="0.01" step="0.01" value={spend.amount}
              onChange={e=>setSpend(s=>({...s,amount:e.target.value}))}/>
          </label>
          <label className="text-sm">Invoice / external transaction ID
            <input className={field} value={spend.source_reference} onChange={e=>setSpend(s=>({...s,source_reference:e.target.value}))}/>
          </label>
          <label className="text-sm">Notes (optional)
            <input className={field} value={spend.description} onChange={e=>setSpend(s=>({...s,description:e.target.value}))}/>
          </label>
        </div>
        <button type="button" className={button+" mt-4"}
          disabled={busy||!campaignId||!spend.amount||Number(spend.amount)<=0||spend.source_reference.length<6}
          onClick={addSpend} data-testid="marketing-record-spend">Record verified spend</button>
        <div className="mt-4">
          <h3 className="text-sm font-semibold">Lead acquisition attribution</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(stats?.inquiries_by_utm_source||{}).map(([source,n])=><span key={source} className="rounded-full border px-3 py-1 text-xs">{source}: {n}</span>)}
            {!Object.keys(stats?.inquiries_by_utm_source||{}).length&&<p className="text-xs text-slate-500">No attributable inquiries.</p>}
          </div>
        </div>
      </div>
    </section>}
   <footer className="flex items-center gap-2 text-xs text-slate-500">
     <ShieldCheck className="h-4 w-4"/> Only reviewed, owner-authorized actions; no background ad spend or outbound campaigns.
   </footer>
 </main>;
}
